/**
 * ProjectCard component - Reusable card for displaying project information
 * Used in project lists and grids
 */

import { BarChart3, Calendar } from 'lucide-react';

import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/utils';
import type { Project } from '@/types';

// ============================================================================
// TYPES
// ============================================================================

export interface ProjectCardProps {
  project: Project;
  onClick?: () => void;
  className?: string;
  showStatus?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function ProjectCard({
  project,
  onClick,
  className,
  showStatus = false,
}: ProjectCardProps) {
  const updatedDate = new Date(project.updatedAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const statusBadgeStyles = {
    active: 'bg-success-soft text-success',
    archived: 'bg-warning-soft text-warning',
    deleted: 'bg-danger-soft text-danger',
  };

  return (
    <Card
      className={cn(
        'transition-all hover:border-border-strong hover:shadow-md cursor-pointer group',
        className
      )}
      onClick={onClick}
    >
      <div className="space-y-4">
        {/* Header with project name and status */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-primary truncate group-hover:text-brand transition-colors">
              {project.name}
            </h3>
            {project.brandName && (
              <p className="text-sm text-secondary mt-1">
                {project.brandName}
              </p>
            )}
          </div>
          {showStatus && (
            <div
              className={cn(
                'inline-flex items-center px-3 h-6 rounded-pill text-xs font-semibold uppercase tracking-wide flex-shrink-0',
                statusBadgeStyles[project.status]
              )}
            >
              {project.status}
            </div>
          )}
        </div>

        {/* Description (if available) */}
        {project.description && (
          <p className="text-sm text-secondary line-clamp-2">
            {project.description}
          </p>
        )}

        {/* Metadata row */}
        <div className="flex items-center gap-6 text-xs text-secondary pt-2 border-t border-border-subtle">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-3.5 w-3.5 text-muted" />
            <span>
              {project.creativeCount ?? 0}{' '}
              {(project.creativeCount ?? 0) === 1 ? 'creative' : 'creatives'}
            </span>
          </div>
          <div className="flex items-center gap-2 text-muted">
            <Calendar className="h-3.5 w-3.5" />
            <span>Updated {updatedDate}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}
